// Routines reads — carved from `src/features/workflows/hooks.ts`, which is now a
// delegate.
//
// History: partial extraction (the 918-line source splits into this module and
// its `mutation` sibling), so `git log --follow` cannot trace it —
// `git log --follow -- src/features/workflows/hooks.ts`.
//
// HEADLESS. Every read hook here composed its own gate before the move:
// `useAuthGatedEnabled(useIsWorkflowsEnabled())` — a feature flag AND
// `useIsAuthenticated()` — with the session-runs hook reading a second flag
// instead. A flag read and an auth read are app policy and may not live in
// `/data`, so the gate crosses as one explicit `enabled` parameter per hook and
// the delegate composes it. That keeps the pre-auth-401 protection the gate
// exists for exactly where it was: a `false` gate means the query never fires.
//
// WHAT STAYED IN THIS ZONE ON PURPOSE, because it is data behaviour rather than
// app policy: the `!!id` guards (`enabled && !!workflowId`), the poll cadence,
// the `select` filters, and `useWorkflowSchedules`' sibling cache seeding. The
// only thing the delegate decides is whether the user is allowed to read at all.
//
// THE CACHE IDENTITIES THIS FILE SPELLS INLINE ARE NOT HOISTED —
// `['workflows', 'schedules-batch', ...sortedIds]`,
// `['workflows', 'event-rules-batch', ...sortedIds]`, the
// `[...WORKFLOW_KEYS.templatesForLocale(locale), 'connector', connector ?? '']`
// suffix, and the bare
// `['workflows', 'list']` prefix in `invalidateWorkflowLists`. Folding them into
// the frozen factory would change a cache identity, which is its own reviewed
// step; doing it inside a move would make a behaviour change wear a tidy-up's
// clothes and there would be no diff line to point at.
import {
  skipToken,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { WORKFLOW_KEYS } from '../../contracts/keys';
import type {
  Workflow,
  WorkflowListQuery,
  WorkflowListResponse,
  WorkflowRun,
} from '../../contracts/routines.types';
import { routinesClient } from '../api/routines';
import {
  clearProjectWorkflowArtifact,
  readRoutineForProject,
  writeProjectArtifact,
  writeRoutineForProject,
} from '../projects/artifacts/projectArtifactPersistence';
import { pendingFirstTurnAtom } from '../projects/pendingFirstTurn';
import { pickLatestRun, runHasChatShell } from '../routines/pickLatestRun';
import { isReleasedPreset } from '../routines/recommended-presets';
import { isRunNonTerminal } from '../routines/run-state';
import { workflowFromPreset } from '../routines/workflow-from-preset';

/**
 * The app-resolved read gate, as it reaches every hook in this module.
 *
 * One boolean and not a bag of flags: what `/data` needs to know is whether
 * this user may read Routines right now, not which flag or which auth provider
 * decided it.
 */
interface RoutineReadGate {
  enabled: boolean;
}

/**
 * Invalidate every Routines list surface. Takes the client rather than calling
 * `useQueryClient` so a mutation's `onSuccess` and an app component can both
 * use it.
 */
export const invalidateWorkflowLists = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
  void qc.invalidateQueries({ queryKey: ['workflows', 'schedules-batch'] });
  void qc.invalidateQueries({ queryKey: ['workflows', 'event-rules-batch'] });
};

/** Invalidate run-history caches after a workflow run changes state. */
export const invalidateWorkflowRunQueries = (qc: QueryClient): void => {
  void qc.invalidateQueries({ queryKey: ['workflows', 'all-runs'] });
  void qc.invalidateQueries({ queryKey: ['workflows', 'runs'] });
  void qc.invalidateQueries({ queryKey: ['workflows', 'session-runs'] });
};

// ── Queries ─────────────────────────────────────────────────────

export const useWorkspaceShareTarget = (workspaceId: string | null) => {
  return useQuery({
    queryKey: ['workflows', 'share-target', workspaceId],
    staleTime: 5 * 60 * 1000,
    queryFn: workspaceId
      ? async () => (await routinesClient.getWorkspaceShareTarget(workspaceId)).data
      : skipToken,
  });
};

export const useWorkflows = ({
  query = {},
  staleTime = 0,
  enabled,
}: RoutineReadGate & { query?: WorkflowListQuery; staleTime?: number }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.list(query),
    enabled,
    staleTime,
    queryFn: async () => (await routinesClient.list(query)).data,
  });
};

/**
 * The routine whose saved instructions are exactly `instructions`, read from the
 * workflow list cache only (never fetches). Lets a transcript row prove a
 * persisted brief came from a real routine before it hides that brief.
 */
export const useCachedWorkflowByInstructions = (
  instructions: string | undefined,
  queryClient?: QueryClient
) => {
  const { data } = useQuery<WorkflowListResponse>(
    { queryKey: WORKFLOW_KEYS.list({}), queryFn: skipToken },
    queryClient
  );
  return useMemo(
    () =>
      instructions
        ? data?.workflows.find(
            (workflow) => workflow.instructions.trim() === instructions
          )
        : undefined,
    [data, instructions]
  );
};

/**
 * @deprecated Explore uses ``useWorkflowTemplates`` + ``workflowFromPreset``.
 * Kept for any leftover system-workflow callers.
 */
export const useSystemWorkflows = ({ enabled }: RoutineReadGate) =>
  useWorkflows({ query: { creator_type: 'system' }, enabled });

export const useWorkflow = ({
  workflowId,
  enabled,
}: RoutineReadGate & { workflowId: string | undefined }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.detail(workflowId ?? ''),
    enabled: enabled && !!workflowId,
    queryFn: async () => (await routinesClient.get(workflowId!)).data,
  });
};

/**
 * The catalog copy is server-localised from the account language, so the
 * active locale is cache identity. Like the read gate, it crosses as an
 * explicit parameter the delegate composes; nothing is sent to the backend.
 */
interface RoutineTemplatesLocale {
  locale: string;
}

/** The Heidi-curated preset catalog shown in the Marketplace gallery. */
export const useWorkflowTemplates = ({
  enabled,
  locale,
}: RoutineReadGate & RoutineTemplatesLocale) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.templatesForLocale(locale),
    enabled,
    queryFn: async () => (await routinesClient.listTemplates()).data,
    select: (data) => data.templates.filter((t) => isReleasedPreset(t.template_id)),
    // Ride the previous catalog across a locale re-key so the gallery never
    // empties mid-switch; locale is the only thing this key varies on.
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Presets bound to a Connected-apps provider (includes hidden integration ones).
 */
export const useConnectorWorkflowTemplates = ({
  connector,
  enabled,
  locale,
}: RoutineReadGate & RoutineTemplatesLocale & { connector: string | undefined }) => {
  return useQuery({
    queryKey: [...WORKFLOW_KEYS.templatesForLocale(locale), 'connector', connector ?? ''],
    enabled: enabled && !!connector,
    queryFn: async () =>
      (await routinesClient.listTemplates({ connector: connector! })).data,
    select: (data) => data.templates.filter((t) => isReleasedPreset(t.template_id)),
    // Previous data rides a locale-only re-key; a connector change is
    // different content and falls through to a real pending state.
    placeholderData: (previousData, previousQuery) => {
      const previousConnector = previousQuery?.queryKey[4];
      return previousConnector === (connector ?? '') ? previousData : undefined;
    },
  });
};

/** Installed (including hidden) workflows for a Connected-apps provider. */
export const useConnectorWorkflows = ({
  connector,
  enabled,
}: RoutineReadGate & { connector: string | undefined }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.list({ connector, include_hidden: true }),
    enabled: enabled && !!connector,
    staleTime: 0,
    queryFn: async () =>
      (
        await routinesClient.list({
          connector: connector!,
          include_hidden: true,
          include_shared: false,
        })
      ).data,
  });
};

/** Seed a detail read from a cached Routines list that already has this id. */
const cachedListWorkflow = (
  qc: QueryClient,
  workflowId: string
): Workflow | undefined => {
  const owned = qc.getQueryData<WorkflowListResponse>(
    WORKFLOW_KEYS.list({ creator_type: 'user' })
  );
  const fromOwned = owned?.workflows.find(
    (workflow) => workflow.workflow_id === workflowId
  );
  if (fromOwned) return fromOwned;

  const lists = qc.getQueriesData<WorkflowListResponse>({
    queryKey: ['workflows', 'list'],
  });
  for (const [, data] of lists) {
    const match = data?.workflows.find((workflow) => workflow.workflow_id === workflowId);
    if (match) return match;
  }
  return undefined;
};

/**
 * Resolve a split-view selection id: persisted workflow from the API, or a
 * marketplace preset adapted into the Workflow card/conversation shape.
 */
export const useResolvableWorkflow = ({
  workflowId,
  enabled,
  locale,
}: RoutineReadGate & RoutineTemplatesLocale & { workflowId: string | undefined }) => {
  const qc = useQueryClient();
  const templates = useWorkflowTemplates({ enabled, locale });

  const fromPreset = useMemo(() => {
    if (!workflowId || !templates.data) return undefined;
    const template = templates.data.find((t) => t.template_id === workflowId);
    return template ? workflowFromPreset(template) : undefined;
  }, [workflowId, templates.data]);

  // Skip GET /workflows/:id for known catalog ids — they aren't workflow rows.
  // placeholderData only covers the in-flight GET; TanStack drops it on
  // error, so the return still serves the list row when the GET fails.
  const seeded = enabled && workflowId ? cachedListWorkflow(qc, workflowId) : undefined;
  const detail = useQuery({
    queryKey: WORKFLOW_KEYS.detail(workflowId ?? ''),
    enabled: enabled && !!workflowId && !fromPreset && !templates.isLoading,
    queryFn: async () => (await routinesClient.get(workflowId!)).data,
    placeholderData: () => seeded,
  });

  return {
    data: fromPreset ?? detail.data ?? (detail.isError ? seeded : undefined),
    isLoading:
      Boolean(workflowId) && (templates.isLoading || (!fromPreset && detail.isLoading)),
    isError: !fromPreset && detail.isError,
  };
};

export const useWorkflowSchedule = ({
  workflowId,
  enabled,
}: RoutineReadGate & { workflowId: string | undefined }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.schedule(workflowId ?? ''),
    enabled: enabled && !!workflowId,
    queryFn: async () => (await routinesClient.getSchedule(workflowId!)).data,
  });
};

export const useWorkflowEventRules = ({
  workflowId,
  enabled,
}: RoutineReadGate & { workflowId: string | undefined }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.eventRules(workflowId ?? ''),
    enabled: enabled && !!workflowId,
    queryFn: async () => (await routinesClient.listEventRules(workflowId!)).data,
  });
};

export const useWorkflowRuns = ({
  workflowId,
  limit,
  enabled,
}: RoutineReadGate & { workflowId: string | undefined; limit?: number }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.runs(workflowId ?? '', limit),
    enabled: enabled && !!workflowId,
    queryFn: async () =>
      (
        await routinesClient.listRuns(
          workflowId!,
          limit === undefined ? undefined : { limit }
        )
      ).data,
  });
};

/**
 * `enabled` here carries BOTH halves: the app-resolved gate and the caller's
 * own opt-out (`options.enabled !== false` at the old path), which lets a
 * surface keep the hook mounted without fetching. The delegate composes the two
 * because both are its inputs — the flag it reads and the argument its caller
 * passed.
 */
export const useAllWorkflowRuns = ({
  workflowId,
  enabled,
}: RoutineReadGate & { workflowId?: string }) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.allRuns(workflowId),
    enabled,
    queryFn: async () =>
      (await routinesClient.listAllRuns({ workflow_id: workflowId })).data,
  });
};

export const getSessionRunsRefetchInterval = (
  runs: WorkflowRun[] | undefined,
  options?: { pollWhileAwaiting?: boolean; pollWhileLive?: boolean }
): 5000 | false => {
  // In-session triggers start runs mid-recording, after the post-create window.
  if (options?.pollWhileLive) return 5_000;
  if (options?.pollWhileAwaiting && (runs?.length ?? 0) === 0) return 5_000;
  const hasNonTerminalSessionRun = runs?.some(
    (run) =>
      (run.trigger_type === 'event_rule' || run.trigger_type === 'on_demand') &&
      isRunNonTerminal(run)
  );
  return hasNonTerminalSessionRun ? 5_000 : false;
};

export const useSessionWorkflowRuns = ({
  sessionId,
  enabled,
  pollWhileAwaitingRuns = false,
  pollWhileLive = false,
}: RoutineReadGate & {
  sessionId: string | undefined;
  pollWhileAwaitingRuns?: boolean;
  pollWhileLive?: boolean;
}) => {
  return useQuery({
    queryKey: WORKFLOW_KEYS.sessionRuns(sessionId ?? ''),
    enabled: enabled && !!sessionId,
    queryFn: async () =>
      (await routinesClient.listAllRuns({ session_id: sessionId, limit: 50 })).data,
    select: (data) => {
      // During the legacy dual-read, the backend cannot filter event-rule runs yet.
      // Include on_demand runs too so manual in-session triggers surface here.
      const runs = data.runs.filter(
        (run) => run.trigger_type === 'event_rule' || run.trigger_type === 'on_demand'
      );
      return { ...data, runs };
    },
    refetchInterval: (query) =>
      getSessionRunsRefetchInterval(query.state.data?.runs, {
        pollWhileAwaiting: pollWhileAwaitingRuns,
        pollWhileLive,
      }),
  });
};

/**
 * Imperatively resolve a routine's most recent openable run (one with a chat
 * project behind it). Shares the ``all-runs`` cache key with the split view;
 * shell-less link rows (no ``project_id``) are skipped. Returns null when the
 * routine has never produced an openable run.
 *
 * No-ops (resolves null without fetching) when the gate is closed. That gate is
 * the routines flag ALONE at the old path — not the auth-gated form the
 * `useQuery` hooks take — because an imperative `fetchQuery` has no `enabled`
 * option for an auth gate to cross through. Preserved as it was: the delegate
 * passes the bare flag here and the composed gate everywhere else.
 */
export const useFetchLatestRunWithProject = ({ enabled }: RoutineReadGate) => {
  const qc = useQueryClient();
  return useCallback(
    async (workflowId: string) => {
      if (!enabled) return null;
      const data = await qc.fetchQuery({
        queryKey: WORKFLOW_KEYS.allRuns(workflowId),
        queryFn: async () =>
          (await routinesClient.listAllRuns({ workflow_id: workflowId })).data,
        staleTime: 15_000,
      });
      return pickLatestRun((data.runs ?? []).filter(runHasChatShell));
    },
    [qc, enabled]
  );
};

export type OwnedRoutineLanding = { kind: 'run'; projectId: string } | { kind: 'no-run' };

export type OwnedRoutineLandingOptions = { openCard: boolean };

/**
 * Resolve where an owned routine should land without coupling package data to
 * routing or UI state. A failed run-history read is indistinguishable from no
 * run to callers, which can then choose their own fallback.
 */
export const useResolveOwnedRoutineLanding = ({ enabled }: RoutineReadGate) => {
  const fetchLatestRun = useFetchLatestRunWithProject({ enabled });

  return useCallback(
    async (
      workflowId: string,
      { openCard }: OwnedRoutineLandingOptions
    ): Promise<OwnedRoutineLanding> => {
      try {
        const latest = await fetchLatestRun(workflowId);
        if (!latest?.project_id) return { kind: 'no-run' };

        // The header resolves a chat from the newest runs only. Remember the
        // pair here so an older run still shows its routine after that window.
        writeRoutineForProject(latest.project_id, workflowId);

        const workflowArtifact = { type: 'workflow' as const, workflowId };
        if (openCard) {
          writeProjectArtifact(latest.project_id, workflowArtifact);
        } else {
          clearProjectWorkflowArtifact(latest.project_id);
        }

        // A seeded first-turn handoff is intentionally retained after its send.
        // When this run is opened again, routed hydration replays that matching
        // record after the artifact host restores session storage. Keep both
        // sources aligned so hydration cannot undo this landing decision.
        getDefaultStore().set(pendingFirstTurnAtom, (pending) =>
          pending?.projectId === latest.project_id
            ? {
                ...pending,
                openArtifact: openCard ? workflowArtifact : null,
              }
            : pending
        );

        return { kind: 'run', projectId: latest.project_id };
      } catch {
        return { kind: 'no-run' };
      }
    },
    [fetchLatestRun]
  );
};

/**
 * Resolve the routine behind a Work project, when the project came from a
 * workflow run. The only place the project↔routine link exists is
 * ``GET /workflows/runs`` (``ProjectDetail`` carries no workflow id), so we
 * match the project against the caller's runs list — same mapping (and same
 * TanStack cache entry) the History view uses. That list is the newest runs
 * only, so a chat opened from Routines also keeps its routine id in session
 * storage; a miss falls back to that record. Returns nulls for plain
 * (non-routine) projects.
 *
 * Scope guard: the runs fetch only fires for users who actually own
 * routines (the workflows list is already cached by the sidebar/routines
 * surfaces), so plain-project chats — the overwhelming majority — never
 * pull run history at all. A per-project reverse lookup
 * (``GET /workflows/runs?project_id=``) needs a backend change; tracked as
 * a follow-up, this client-side mapping is interim.
 */
export const useRoutineForProject = ({
  projectId,
  enabled,
}: RoutineReadGate & { projectId: string | undefined }) => {
  const { data: workflowsData } = useWorkflows({ enabled });
  const hasRoutines = (workflowsData?.workflows ?? []).length > 0;
  const { data: runsData } = useAllWorkflowRuns({
    enabled: enabled && Boolean(projectId) && hasRoutines,
  });
  const run = useMemo(
    () =>
      projectId
        ? ((runsData?.runs ?? []).find((r) => r.project_id === projectId) ?? null)
        : null,
    [projectId, runsData?.runs]
  );
  const matchedWorkflowId =
    typeof run?.source_workflow_id === 'string' && run.source_workflow_id.length > 0
      ? run.source_workflow_id
      : null;
  const rememberedWorkflowId =
    projectId && !matchedWorkflowId ? readRoutineForProject(projectId) : null;
  const workflowId = matchedWorkflowId ?? rememberedWorkflowId ?? undefined;

  useEffect(() => {
    if (projectId && matchedWorkflowId) {
      writeRoutineForProject(projectId, matchedWorkflowId);
    }
  }, [projectId, matchedWorkflowId]);

  const { data: workflow } = useWorkflow({
    workflowId,
    enabled,
  });
  return { workflow: workflow ?? null, run };
};

/** Fetch schedules for multiple workflows in a single batch request. */
export const useWorkflowSchedules = ({
  workflowIds,
  enabled,
}: RoutineReadGate & { workflowIds: string[] }) => {
  const qc = useQueryClient();
  const sortedIds = useMemo(() => [...workflowIds].sort(), [workflowIds]);

  const {
    data: schedulesMap,
    isLoading: isPending,
    isError,
  } = useQuery({
    queryKey: ['workflows', 'schedules-batch', ...sortedIds],
    enabled: enabled && sortedIds.length > 0,
    queryFn: async () => {
      const resp = await routinesClient.getSchedulesBatch(sortedIds);
      const map = resp.data.schedules;
      for (const id of sortedIds) {
        qc.setQueryData(WORKFLOW_KEYS.schedule(id), map[id] ?? null);
      }
      return map;
    },
  });

  const schedules = useMemo(
    () =>
      workflowIds.map((id) => ({
        data: schedulesMap?.[id],
      })),
    [workflowIds, schedulesMap]
  );

  return { data: schedulesMap, schedules, isPending, isError };
};

/** Fetch event rules for multiple workflows in a single batch request. */
export const useWorkflowEventRulesBatch = ({
  workflowIds,
  enabled,
}: RoutineReadGate & { workflowIds: string[] }) => {
  const qc = useQueryClient();
  const sortedIds = useMemo(() => [...workflowIds].sort(), [workflowIds]);

  const { data: eventRulesMap, isLoading: isPending } = useQuery({
    queryKey: ['workflows', 'event-rules-batch', ...sortedIds],
    enabled: enabled && sortedIds.length > 0,
    queryFn: async () => {
      const resp = await routinesClient.getEventRulesBatch(sortedIds);
      const map = resp.data.event_rules;
      for (const [id, rules] of Object.entries(map)) {
        qc.setQueryData(WORKFLOW_KEYS.eventRules(id), {
          event_rules: rules,
          total: rules.length,
        });
      }
      return map;
    },
  });

  const eventRules = useMemo(
    () =>
      workflowIds.map((id) => ({
        data: eventRulesMap?.[id],
      })),
    [workflowIds, eventRulesMap]
  );

  return { eventRules, isPending };
};

/** Convenience: get all instructed (non-skill) workflows for the slash picker. */
export const useChatWorkflows = ({ enabled }: RoutineReadGate) =>
  useWorkflows({ query: { source: 'instructed' }, enabled });
